// ── Heartbeat de presença ─────────────────────────────────────────
// Marca uma janela de presença no boot, quando a aba volta a ficar
// visível e em INTERAÇÃO do usuário, com throttle de USAGE_PING_MINUTES.
//
// Sem setInterval de propósito, mas não por economia: um timer contaria
// presença de quem deixou o CRM aberto num monitor e foi embora. Amarrar
// o ping a interação real resolve os dois lados — quem trabalha o dia
// inteiro na mesma aba (e nunca dispara visibilitychange) é contado, e
// aba aberta e ociosa não é.

import { useEffect, useRef } from 'react';
import { touchActiveDay } from '@app';
import { USAGE_PING_MINUTES } from '@config/usageTracking';
import { isEnterpriseWorkspace } from '@config/enterprise';

const THROTTLE_MS = USAGE_PING_MINUTES * 60_000;

export function useUsageHeartbeat(
  userId: string | null | undefined,
  workspaceId: string | null | undefined,
  workspace: unknown,
): void {
  // Só o Enterprise consome o painel de uso; não medir os demais evita
  // escrita inútil em workspace que nunca vai ler o dado.
  const enabled = Boolean(userId && workspaceId && isEnterpriseWorkspace(workspace));

  // Espelho em memória do throttle. Os listeners de interação disparam a
  // cada clique/tecla; conferir aqui evita ler o localStorage toda vez.
  const lastBeatRef = useRef(0);

  useEffect(() => {
    if (!enabled || !workspaceId) return;

    const key = `usageHeartbeat:${workspaceId}:${userId}`;

    const beat = () => {
      const now = Date.now();
      if (now - lastBeatRef.current < THROTTLE_MS) return;

      let last = 0;
      try {
        last = Number(localStorage.getItem(key) || 0);
      } catch {
        // localStorage indisponível (modo privado/quota): segue e pinga.
      }
      if (Number.isFinite(last) && now - last < THROTTLE_MS) {
        // Já pingamos nesta janela noutra aba/carga — sincroniza o espelho.
        lastBeatRef.current = last;
        return;
      }

      lastBeatRef.current = now;
      try {
        localStorage.setItem(key, String(now));
      } catch {
        // Sem persistência do throttle o ping repete mais que o ideal —
        // aceitável, não vale bloquear a medição por isso.
      }
      // Telemetria nunca derruba a UI.
      void touchActiveDay(workspaceId).catch(() => {});
    };

    beat();

    const onVisible = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVisible);

    // Interação real do usuário. `wheel` cobre quem só lê o Kanban sem
    // clicar. Passivos e baratos: o guard do ref corta antes de qualquer
    // trabalho fora da janela de throttle.
    const opts = { passive: true } as const;
    window.addEventListener('pointerdown', beat, opts);
    window.addEventListener('keydown', beat, opts);
    window.addEventListener('wheel', beat, opts);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pointerdown', beat);
      window.removeEventListener('keydown', beat);
      window.removeEventListener('wheel', beat);
    };
  }, [enabled, userId, workspaceId]);
}
